import { Picker, PickerItem, PropertyValue } from '@easylife365/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { TaskAssigneeDto } from '../../../api/app.types';
import { graphQueries } from '../../../api/graph.api';
import { sameId } from '../../../lib/ids';

type AssigneePickerProps = {
  assignees: TaskAssigneeDto[];
  disabled: boolean;
  formLabel: string;
};

const toItem = ({ id, displayName, mail, userPrincipalName }: TaskAssigneeDto): PickerItem => ({
  id,
  displayName,
  mail,
  userPrincipalName,
  isLoading: false,
});

const matches = ({ displayName, mail, userPrincipalName }: TaskAssigneeDto, term: string) =>
  [displayName, mail, userPrincipalName].some((value) => value?.toLowerCase().includes(term));

/**
 * Offers only the people the server will accept as the assignee of a task, which Graph cannot tell the browser. The
 * list is read once and searched here. An assignee stored before the rule existed may not be in it, so a stored
 * value is resolved through Graph and is kept as it is until the owner changes it.
 */
const AssigneePicker: React.FC<AssigneePickerProps> = ({ assignees, disabled, formLabel }) => {
  const queryClient = useQueryClient();

  const onSearch = useCallback(
    (value: string) => {
      const term = value.trim().toLowerCase();
      return Promise.resolve((term ? assignees.filter((assignee) => matches(assignee, term)) : assignees).map(toItem));
    },
    [assignees]
  );

  const onLookup = useCallback(
    async (id: string) => {
      const listed = assignees.find((assignee) => sameId(assignee.id, id));
      if (listed) return toItem(listed);

      const user = await queryClient.fetchQuery(graphQueries.getUserById(id));
      if (!user) return undefined;

      return {
        id: user.id,
        displayName: user.displayName ?? undefined,
        mail: user.mail ?? undefined,
        userPrincipalName: user.userPrincipalName ?? undefined,
        isLoading: false,
      };
    },
    [assignees, queryClient]
  );

  return (
    <Picker
      name="ownerId"
      formLabel={formLabel}
      disabled={disabled}
      targetPropertyValue={PropertyValue.Id}
      onSearch={onSearch}
      onLookup={onLookup}
    />
  );
};

export default AssigneePicker;
